import moment from 'jalali-moment'

/**
 * A loan request, handed to the اعطای وام wizard as its already-chosen
 * borrower.
 *
 * «بررسی و اعطای وام» used to open the wizard on «به چه کسی وام می‌دهید؟» —
 * from the page of ONE request, asking the manager to find that same member
 * again in a list (owner, 2026-09-11). The wizard already knows how to start
 * from a handed-over borrower (کارنامهٔ عضو does it through router state), so
 * this builds that borrower out of what the request page already has, and the
 * wizard opens on step 2 with the amount and instalments prefilled.
 *
 * Router state rather than the `?borrower=` link the Bale nudge uses: that
 * path fetches the whole-fund ranking to find the row — several seconds on a
 * large fund, during which step 2 sits there empty — and the ranking carries
 * the member's OLDEST open request, which is not necessarily the one being
 * reviewed. This carries exactly this request's id, so it is this request that
 * closes as accepted.
 *
 * Only the fields the wizard reads are real; `score`/`rank` feed analytics
 * only and are left unset.
 */
export interface IDemandHandover {
    demandId: number
    poolshipId: number
    memberId: number
    memberName: string
    amount: number
    numRepayments: number
    priority: string
    description: string
    /** Jalali YYYY-MM-DD, the form the ranking payload uses */
    requestedJalali: string
    /** the account the member picked when filing; preselected in step 5 */
    cardId: number | null
}

const toLatinDigits = (value: string) =>
    value.replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))

/** An ISO/Gregorian timestamp, as the Jalali date the wizard expects. */
export const jalaliDateOf = (value: string | Date) =>
    toLatinDigits(moment(value).locale('fa').format('YYYY-MM-DD'))

const waitingDays = (requestedJalali: string) => {
    const filed = moment.from(requestedJalali, 'fa', 'YYYY-MM-DD')
    if (!filed.isValid()) return 0
    return Math.max(
        0,
        moment().startOf('day').diff(filed.clone().startOf('day'), 'days')
    )
}

export const candidateFromDemand = (demand: IDemandHandover): ILoanCandidate =>
    ({
        poolship_id: demand.poolshipId,
        member_id: demand.memberId,
        member_name: demand.memberName,
        demand: {
            id: demand.demandId,
            amount: demand.amount,
            num_repayments: demand.numRepayments,
            priority: demand.priority,
            requested_date: demand.requestedJalali,
            waiting_days: waitingDays(demand.requestedJalali),
            description: demand.description,
            // only the id is read (to preselect the account in step 5); the
            // digits come from the account list the review step loads itself
            selected_card: demand.cardId
                ? ({
                      id: demand.cardId,
                      member: demand.memberId,
                  } as NonNullable<ILoanCandidate['demand']>['selected_card'])
                : null,
        },
    }) as ILoanCandidate

/** Where to navigate, and with what, to grant this request. */
export const grantRouteFor = (
    fundId: string | number,
    demand: IDemandHandover,
    source: 'cartable' | 'requests_list',
    fundName?: string
) => ({
    to: `/loans/management/grant/${fundId}?src=${source}`,
    state: {
        borrower: candidateFromDemand(demand),
        source,
        name: fundName,
    },
})
